import pluralize from "pluralize"
import {
  AdvancedSearchConfigurationsType,
  ConfigurationsType,
  DependenceType,
  MissionConfigurationsType,
  NumberSearchFieldType,
  SearchFieldType,
} from "../types/configurations"
import { CircleShapeType, ShapeType } from "../types/shape"
import { isACircleShape } from "./shapeHelper"
import { fromISOStringToUTCMoment, getMomentDaysOffset } from "./dateHelper"
import {
  AllInterferometricStacksType,
  ProductImageStacType,
} from "../types/product"
import { CartOrderType } from "../types/cart"
import {
  extractFiltersBy,
  extractFinalValues,
  hasBeenFilled,
  onlyUnique,
} from "./dynamicFormHelper"
import { getSortFieldsFrom } from "./searchResultsHelper"
import {
  getAllInterferometricStacksSelected,
  getStripProductImages,
} from "./interferometricStackHelper"
import translations from '../translations';
import {AppParametersType} from "../types/app";
import App from "../components/App";
import jwt_decode from "jwt-decode";

export const ADVANCED_SEARCH_SCOPE_CONFIGURATION_FIELDS = "advanced-search"
export const SEARCH_SCOPE_CONFIGURATION_FIELDS = "search"

export const isUpdateResultsSearchAvailable = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  formValues: any,
  selectedProducts: ProductImageStacType[]
): boolean => {
  return isSatelliteImagery(configurations, appParameters) &&
      formValues.routine &&
      formValues &&
      formValues.missions &&
      formValues.missions.length === 1 &&
      selectedProducts.length > 0 &&
      selectedProducts.every((product) =>
          getMissionCodesByStacItem(configurations, product).includes(
              formValues.missions[0]
          )
  )
}


export const isUpdateResultsSearchModeAvailable = (formValues: any): boolean =>
  !formValues.routine

export const isSearchingForProductId = (formValues: any): boolean =>
  formValues.search_mode

export const isRequiredConvexPolygon = (
  configurations: ConfigurationsType,
  formValues
): boolean => false

export const drawInscribedPolygon = (
    configurations: ConfigurationsType
): boolean => false

export const hasMadeAMultiMissionsSearch = (
  configurations: ConfigurationsType,
  formValues
): boolean =>
  isMultiMissionsAvailable(configurations) &&
  formValues &&
  formValues.missions &&
  formValues.missions.length > 1

export const isMultiMissionsAvailable = (
  configurations: ConfigurationsType
): boolean => true

export const isSatelliteImagery = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType
): boolean => {
  const activeFlow = hasActiveFlow(appParameters);
  return (activeFlow === false);
}

export const isInvalidation = (
    configurations: ConfigurationsType,
    appParameters: AppParametersType
): boolean => {
  const activeFlow = hasActiveFlow(appParameters);
  return (activeFlow !== false && activeFlow.id === "invalidation");
}

export const isReprocessing = (
    configurations: ConfigurationsType,
    appParameters: AppParametersType
): boolean => {
  const activeFlow = hasActiveFlow(appParameters);
  return (activeFlow !== false && activeFlow.id === "reprocessing");
}

export const isInvalidationOrReprocessing = (
    configurations: ConfigurationsType,
    appParameters: AppParametersType
): boolean => {
  const activeFlow = hasActiveFlow(appParameters);
  return (activeFlow !== false && (activeFlow.id === "reprocessing" || activeFlow.id === "invalidation"));
}



export const hasOneMissionSelected = (formValues: any = {}): boolean =>
  "missions" in formValues && formValues.missions.length === 1

export const hasAtLeastOneMissionSelected = (formValues: any = {}): boolean =>
  "missions" in formValues && formValues.missions.length > 0

export const hasNoMissionSelected = (formValues: any = {}): boolean =>
  "missions" in formValues && formValues.missions.length === 0

export const getSearchFormValuesByState = (state) => {
  const {
    application: { configurations, formData, routine },
  } = state
  const formValues = formData || {
    routine,
  }
  return formValues.missions && "missions" in configurations
    ? {
        ...formValues,
        missions: getMissionCodesByDescriptions(
          configurations,
          formValues.missions
        ),
      }
    : { ...formValues }
}

export const getSyncSearchFormValuesByState = (state) => {
  const {
    application: { configurations, missionSelected, formData, routine },
  } = state
  const formValues = formData || {
    routine,
  }
  return missionSelected && "missions" in configurations
    ? { ...formValues, missions: missionSelected }
    : { ...formValues }
}

export const isHandleExternalSearchField = (
  configurations,
  appParameters,
  scopeFields,
  fieldName,
  values,
  lastShape
): boolean => {
  const searchFieldConfig = getSearchFieldConfigurationsBy(
    configurations,
    appParameters,
    scopeFields,
    fieldName,
    values
  )
  return (
    !!searchFieldConfig &&
    isApplicableFieldFromConfigurations(
      configurations,
      appParameters,
      scopeFields,
      fieldName,
      values,
      lastShape
    )
  )
}

export const getScopeConfigurationFieldsBy = (
  configurations: ConfigurationsType
): string => ADVANCED_SEARCH_SCOPE_CONFIGURATION_FIELDS;

export const isApplicableField = (
  fieldConfig
): boolean =>
  fieldConfig.isApplicable

export const isMandatoryField = (
  fieldConfig
): boolean =>
    fieldConfig.isMandatory

export const isDisabledField = (
    fieldConfig
): boolean =>
    fieldConfig.hasOwnProperty('isDisabled') && fieldConfig.isDisabled

export const isMultiSelectionField = (
  fieldConfig
): boolean =>
  fieldConfig.isMultiSelection

export const getAvailableDependencies = (
  dependenciesConfigurations,
  formValues
): DependenceType[] => {
  let dependencies = []
  if (dependenciesConfigurations) {
    if (!!dependenciesConfigurations["All"]) {
      dependencies = dependencies.concat(dependenciesConfigurations["All"])
    }
    if (formValues.routine && !!dependenciesConfigurations["Routine"]) {
      dependencies = dependencies.concat(dependenciesConfigurations["Routine"])
    }
    if (!formValues.routine && !!dependenciesConfigurations["Ondemand"]) {
      dependencies = dependencies.concat(dependenciesConfigurations["Ondemand"])
    }
  }
  return dependencies
}

export const getActiveDependencies = (
  fieldName,
  searchFieldConfiguration,
  formValues,
  selectedArea,
  isProductionField = false
): DependenceType[] => {
  return getAvailableDependencies(
    searchFieldConfiguration.dependences,
    formValues
  ).filter((dependence) =>
    isActiveDependence(
      dependence,
      fieldName,
      searchFieldConfiguration,
      formValues,
      selectedArea,
      isProductionField
    )
  )
}

const isActiveDependence = (
  dependence: DependenceType,
  currentFieldName: string,
  searchField: SearchFieldType,
  formValues,
  selectedArea: ShapeType,
  isProductionField = false
): boolean => {
  let isActive = false
  if (
    dependence &&
    !!dependence.values &&
    !!dependence.constrains &&
    dependence.constrains.name === currentFieldName 
  ) {
    let fieldValue
    if (searchField.description === "EXTERNAL") {
      switch (searchField.name) {
        case "ROUTINE":
          fieldValue = formValues.routine.toString().toUpperCase()
          break
        case "CIRCLE_RADIUS":
          if (isACircleShape(selectedArea)) {
            fieldValue = (selectedArea as CircleShapeType).circle_radius / 1000
          }
          break
        case "AOI_AREA":
          fieldValue = selectedArea && selectedArea.area / 1000000
          break
        case "DAYS_OFFSET":
          if (formValues.date1 && formValues.date2) {
            const fromDate = fromISOStringToUTCMoment(formValues.date1)
            const toDate = fromISOStringToUTCMoment(formValues.date2)
            fieldValue = getMomentDaysOffset(fromDate, toDate)
          }
          break
        default:
          fieldValue = null
      }
    } else {
      fieldValue = formValues[searchField.name]
    }

    if (!!fieldValue) {
      const fieldValuesToVerify = Array.isArray(fieldValue)
        ? fieldValue
        : [fieldValue]

      if (isProductionField) {
        // some deve essercene almeno uno
        isActive = fieldValuesToVerify.some((fv) => {
          let isAct = false
          if (hasBeenFilled(fv)) {
            isAct = true
            if (dependence.values.possibleValues) {
              isAct = isAct && dependence.values.possibleValues.includes(fv)
            }

            if (dependence.values.minValue) {
              isAct = isAct && dependence.values.minValue <= fv
            }

            if (dependence.values.maxValue) {
              isAct = isAct && dependence.values.maxValue >= fv
            }
          }
          return isAct
        });
      } else {
        // every devono esserci tutti
        isActive = fieldValuesToVerify.every((fv) => {
          let isAct = false
          if (hasBeenFilled(fv)) {
            isAct = true
            if (dependence.values.possibleValues) {
              isAct = isAct && dependence.values.possibleValues.includes(fv)
            }

            if (dependence.values.minValue) {
              isAct = isAct && dependence.values.minValue <= fv
            }

            if (dependence.values.maxValue) {
              isAct = isAct && dependence.values.maxValue >= fv
            }
          }
          return isAct
        });
      }
    }
  }
  return isActive
}

export const isMandatoryFieldFromConfigurations = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldName: string,
  formValues,
  selectedArea: ShapeType
): boolean => {
  let isMandatory = false
  let mandatoryDependencies: boolean[] = []
  const searchFieldsConfigurations = getSearchFieldsConfigurationsBy(
    configurations,
    appParameters,
    scopeFields,
    formValues
  )
  if (searchFieldsConfigurations && searchFieldsConfigurations.length > 0) {
    searchFieldsConfigurations.forEach((searchField) => {
      if (searchField.name === fieldName) {
        isMandatory = isMandatoryField(searchField)
      } else {
        const dependencies = getActiveDependencies(
          fieldName,
          searchField,
          formValues,
          selectedArea
        )
        if (dependencies.length > 0) {
          mandatoryDependencies = mandatoryDependencies.concat(
            dependencies.map((dependence: DependenceType) =>
              isMandatoryField(dependence.constrains)
            )
          )
        }
      }
    })
  }

  if (mandatoryDependencies.length > 0) {
    isMandatory = mandatoryDependencies.some((val) => val)
  }

  return isMandatory
}

export const isDisabledFieldFromConfigurations = (
    configurations: ConfigurationsType,
    appParameters: AppParametersType,
    scopeFields: string,
    fieldName: string,
    formValues,
    selectedArea: ShapeType
): boolean => {
  let isDisabled = false
  let disabledDependencies: boolean[] = []
  const searchFieldsConfigurations = getSearchFieldsConfigurationsBy(
      configurations,
      appParameters,
      scopeFields,
      formValues
  )
  if (searchFieldsConfigurations && searchFieldsConfigurations.length > 0) {
    searchFieldsConfigurations.forEach((searchField) => {
      if (searchField.name === fieldName) {
        isDisabled = isDisabledField(searchField)
      } else {
        const dependencies = getActiveDependencies(
            fieldName,
            searchField,
            formValues,
            selectedArea
        )
        if (dependencies.length > 0) {
          disabledDependencies = disabledDependencies.concat(
              dependencies.map((dependence: DependenceType) =>
                  isDisabledField(dependence.constrains)
              )
          )
        }
      }
    })
  }

  if (disabledDependencies.length > 0) {
    isDisabled = disabledDependencies.some((val) => val)
  }

  return isDisabled
}

export const isApplicableFromConfig = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldName: string,
  formValues,
  selectedArea: ShapeType
): boolean => {
  let isApplicable = false
  const searchFieldsConfigurations = getSearchFieldsConfigurationsBy(
    configurations,
    appParameters,
    scopeFields,
    formValues
  )
  if (searchFieldsConfigurations && searchFieldsConfigurations.length > 0) {
    searchFieldsConfigurations.forEach((searchField) => {
      if (searchField.name === fieldName) {
        isApplicable = isApplicableField(searchField)
      }
    })
  }

  return isApplicable
}

export const isApplicableFieldFromConfigurations = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldName: string,
  formValues,
  selectedArea: ShapeType
): boolean => {
  let isApplicable = true
  let applicableDependencies: boolean[] = []
  const searchFieldsConfigurations = getSearchFieldsConfigurationsBy(
    configurations,
    appParameters,
    scopeFields,
    formValues
  )
  if (searchFieldsConfigurations && searchFieldsConfigurations.length > 0) {
    searchFieldsConfigurations.forEach((searchField) => {
      if (searchField.name === fieldName) {
        isApplicable = isApplicableField(searchField)
      } else {
        const dependencies = getActiveDependencies(
          fieldName,
          searchField,
          formValues,
          selectedArea
        )
        if (dependencies && dependencies.length > 0) {
          applicableDependencies = applicableDependencies.concat(
            !dependencies.some(
              (dependence: DependenceType) =>
                !isApplicableField(dependence.constrains)
            )
          )
        }
      }
    })
  }

  if (applicableDependencies.length > 0) {
    isApplicable = applicableDependencies.every((val) => val)
  }

  return isApplicable
}

export const isMultiSelectionFieldFromConfigurations = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldName: string,
  formValues,
  selectedArea: ShapeType
): boolean => {
  const searchField = getSearchFieldConfigurationsBy(
    configurations,
    appParameters,
    scopeFields,
    fieldName,
    formValues
  )
  return isMultiSelectionField(searchField)
}

//////////////////////////////////////////////////////////////
///////////    AdvancedSearchConfigurationsType   ////////////
//////////////////////////////////////////////////////////////

export const getMissionsConfigurationsBy = (
  configurations: AdvancedSearchConfigurationsType,
  missionCodes: string
): MissionConfigurationsType[] =>
  configurations.missions.filter((mission) =>
    missionCodes.includes(mission.code)
  )

export const getMissionConfigurationsBy = (
  configurations: AdvancedSearchConfigurationsType,
  missionCode: string
): MissionConfigurationsType | undefined =>
  configurations.missions.find((mission) => mission.code === missionCode)

export const getMissionDescriptionsByCodes = (
  configurations: AdvancedSearchConfigurationsType,
  missionCodes: string[]
): string[] => {
  return configurations.missions
    .filter((mission) => missionCodes.includes(mission.code))
    .map((mission) => mission.description)
}

export const getMissionDataByCodes = (
    configurations: AdvancedSearchConfigurationsType,
    missionCodes: string[],
    dataset: string|undefined = undefined
): any[] => {
    return configurations.missions
        .filter((mission) => missionCodes.includes(mission.code))
        .map((mission) => {
          let datasetIndex = 0;
          if (dataset) {
            const datasetIndexes = mission.fields["advanced-search"]
                .find(field => field.description === 'Dataset' && field.name === 'dataset')?.possibleValues;
            if (datasetIndexes) {
              datasetIndex = datasetIndexes ? datasetIndexes.findIndex(possibleDataset => possibleDataset === dataset) : 0
            }
          }
          let index = "";
          // const allIndex = mission.fields["advanced-search"]
          //     .find(field => field.description === 'EXTERNAL' && field.name === 'ES_INDEX')?.possibleValues;
          // if (allIndex) {
          //   index = allIndex[datasetIndex];
          // }
          if (mission.index && mission.index !== "") {
            index = mission.index;
            if (mission.index.includes(";")) {
              index = mission.index.split(";")[datasetIndex];
            }
          }
          return {
            description: mission.description,
            index
          }
        });
}

export const getMissionCodesByDescriptions = (
  configurations: AdvancedSearchConfigurationsType,
  missionDescriptions: string[]
): string[] => {
  return configurations.missions
    .filter((mission) => missionDescriptions.includes(mission.description))
    .map((mission) => mission.code)
}

export const getMissionCodesByStacItem = (
  configurations: ConfigurationsType,
  stacItem
): string[] => {
  return (
    (stacItem &&
      stacItem.properties &&
      stacItem.properties.mission &&
      "missions" in configurations &&
      getMissionCodesByDescriptions(
        configurations as AdvancedSearchConfigurationsType,
        [stacItem.properties.mission]
      )) ||
    []
  )
}

export const getFormValuesFromFormData = (
  configurations: ConfigurationsType,
  formData: any = {}
): any => {
  let formValues: any = { ...formData }

  if (formValues.missions && formValues.missions.length > 0) {
    const advancedSearchConfigurations =
      configurations as AdvancedSearchConfigurationsType
    const missionCodes = getMissionCodesByDescriptions(
      advancedSearchConfigurations,
      formValues.missions
    )
    formValues = {
      ...formValues,
      missions: missionCodes,
    }
  }

  return formValues
}

export const getMissionCodesByOrder = (
  configurations: ConfigurationsType,
  order: CartOrderType
): string[] => {
  return order.items
    .map((item) => getMissionCodesByStacItem(configurations, item.metadata))
    .flat()
    .flat()
    .filter((el) => !!el)
    .filter(onlyUnique)
}

export const getAvailableMissions = (
  configurations: AdvancedSearchConfigurationsType,
  formValues
): MissionConfigurationsType[] => {
  return formValues.routine
    ? configurations.missions
    : configurations.missions.filter(
        (mission) => mission.isApplicable
      )
}

export const getAvailableMissionCodes = (
  configurations: AdvancedSearchConfigurationsType,
  formValues
): string[] => {
  return getAvailableMissions(configurations, formValues).map(
    (mission) => mission.code
  )
}

export const getSearchFieldsConfigurationsBy = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues
): SearchFieldType[] => {
  let searchFieldsConfigurations: SearchFieldType[] = []
  if ("missions" in configurations) {
    if (formValues.missions && formValues.missions.length === 1) {
      const missionConfig = getMissionConfigurationsBy(
        configurations as AdvancedSearchConfigurationsType,
        formValues.missions[0]
      )
      if (
        missionConfig &&
        missionConfig.fields &&
        missionConfig.fields[scopeFields]
      ) {
        searchFieldsConfigurations = missionConfig.fields[scopeFields]
      }
    } else {
      // filtri in comune tra tutte le missions
      const searchFieldsConfigurationsList = {};
      formValues.missions.forEach(mission => {
        const missionConfig = getMissionConfigurationsBy(
            configurations as AdvancedSearchConfigurationsType,
            mission
        );
        if (
            missionConfig &&
            missionConfig.fields &&
            missionConfig.fields[scopeFields]
        ) {
          missionConfig.fields[scopeFields].forEach(scopeField => {
            let index = searchFieldsConfigurations.length-1;
            let mixedScopeField = {...scopeField};

            if (searchFieldsConfigurationsList.hasOwnProperty(mixedScopeField.name)) { // il campo esiste già, devo fare un mix (sensato)
              index = searchFieldsConfigurationsList[mixedScopeField.name];
              // faccio il mio mix...
              mixedScopeField = composeFilters(searchFieldsConfigurations[index], mixedScopeField);
              // lo sovrascrivo nella lista
              searchFieldsConfigurations[index] = mixedScopeField;
            } else {
              searchFieldsConfigurations.push(mixedScopeField);
              index++;
            }
            // lo salvo nella lista
            searchFieldsConfigurationsList[mixedScopeField.name] = index;
          });
        }
      });
    }
  } else if ("search" in configurations.fields) {
    searchFieldsConfigurations = configurations.fields["search"]
  }

  let acceptedFields: null | string[] = null;
  let forcedMandatoryFields: null | string[] = null;
  const activeFlow = hasActiveFlow(appParameters);
  if (activeFlow !== false) {
    acceptedFields = activeFlow.acceptedFields
    forcedMandatoryFields = activeFlow.forcedMandatoryFields
  }
  searchFieldsConfigurations = searchFieldsConfigurations.filter((searchField) =>
      (acceptedFields === null || acceptedFields.some((field) => field === searchField.name)));

  searchFieldsConfigurations.forEach((searchField) => {
    if (forcedMandatoryFields !== null && forcedMandatoryFields.some((field) => field === searchField.name)) {
      searchField.isMandatory = true;
    }
  });
  return searchFieldsConfigurations
}

export const composeFilters = (existing, composing) => {
  const result = {...composing}; // parto dal nuovo campo, quindi tutto quello che non viene sovrascritto da existing, è preso da composing
  if (existing.hasOwnProperty('possibleValues') && existing.possibleValues) {
    result.possibleValues = existing.possibleValues?.concat(composing.possibleValues);
  }
  if (existing.hasOwnProperty('maxValue') && existing.hasOwnProperty('minValue') && composing.hasOwnProperty('maxValue') && composing.hasOwnProperty('minValue')) {
    if (existing.operator === composing.operator) {
      result.maxValue = Math.abs(existing.maxValue) > Math.abs(composing.maxValue) ? existing.maxValue : composing.maxValue;
      result.minValue = Math.abs(existing.minValue) > Math.abs(composing.minValue) ? existing.minValue : composing.minValue;
    }
  }
  result.isApplicable = existing.isApplicable || composing.isApplicable;
  result.isMandatory = existing.isMandatory || composing.isMandatory;
  result.search = existing.search || composing.search;
  result.prodParam = existing.prodParam || composing.prodParam;
  result.isMultiSelection = existing.isMultiSelection || composing.isMultiSelection;
  if (existing.hasOwnProperty('dependences') && existing.dependences.hasOwnProperty('All')) {
    if (composing.hasOwnProperty('dependences') && composing.dependences.hasOwnProperty('All')) {
      composing.dependences['All'] = existing.dependences['All'].concat(composing.dependences['All']); // le unisco... non ha senso fare altro in questa fase
    } else {
      composing.dependences = {'All': existing.dependences['All']}; // le copio da existing, semplicemente
    }
  }
  return result;
}

export const getInternalSearchFieldsConfigurationsBy = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues
): SearchFieldType[] => {
  let acceptedFields: null | string[] = null;
  let forcedMandatoryFields: null | string[] = null;
  const activeFlow = hasActiveFlow(appParameters);
  if (activeFlow !== false) {
    acceptedFields = activeFlow.acceptedFields
    forcedMandatoryFields = activeFlow.forcedMandatoryFields
  }
  const searchFieldsConfigurations = getSearchFieldsConfigurationsBy(
      configurations,
      appParameters,
      scopeFields,
      formValues
  );
  const internalSearchFields = searchFieldsConfigurations.filter((searchField) =>
      (acceptedFields === null || acceptedFields.some((field) => field === searchField.name)) &&
      searchField.description !== "EXTERNAL");

  internalSearchFields.forEach((searchField) => {
    if (forcedMandatoryFields !== null && forcedMandatoryFields.some((field) => field === searchField.name)) {
      searchField.isMandatory = true;
    }
  });
  return internalSearchFields;
}
export const getProductionSearchFieldsConfigurationsBy = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues
): SearchFieldType[] =>
  getSearchFieldsConfigurationsBy(
    configurations,
    appParameters,
    scopeFields,
    formValues
  ).filter((searchField) => isProductionSearchField(searchField))

export const isProductionSearchField = (
  searchField: SearchFieldType,
): boolean =>
    !!searchField.prodParam

///// RETURN SearchFieldType

export const getSearchFieldConfigurationsBy = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldName: string,
  formValues
): SearchFieldType | undefined =>
  getSearchFieldsConfigurationsBy(configurations, appParameters, scopeFields, formValues).find(
    (searchField) => searchField.name === fieldName
  )

interface CircleSettingsType {
  maxRadiusTriggererName?: string
  maxRadius?: number
  minRadiusTriggererName?: string
  minRadius?: number
}

export const getCircleShapeSettingsBy = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues,
  selectedArea: CircleShapeType
): CircleSettingsType => {
  let settings: CircleSettingsType = {}

  const constraints = getActiveConstraintsByFieldName(
    configurations,
    appParameters,
    scopeFields,
    formValues,
    selectedArea,
    "CIRCLE_RADIUS"
  )
  constraints.forEach((constraint) => {
    if (
      "maxValue" in constraint &&
      (settings.maxRadius === undefined ||
        settings.maxRadius > constraint.maxValue)
    ) {
      settings = {
        ...settings,
        maxRadius: constraint.maxValue,
        maxRadiusTriggererName: constraint.triggererName,
      }
    }
    if (
      "minValue" in constraint &&
      (settings.minRadius === undefined ||
        settings.minRadius < constraint.minValue)
    ) {
      settings = {
        ...settings,
        minRadius: constraint.minValue,
        minRadiusTriggererName: constraint.triggererName,
      }
    }
  })

  return settings
}

interface ShapeSettingsType {
  maxAreaTriggererName?: string
  maxArea?: number
  minAreaTriggererName?: string
  minArea?: number
}
export const getShapeSettingsBy = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues,
  selectedArea: ShapeType
): ShapeSettingsType => {
  let settings: ShapeSettingsType = {}

  const constraints = getActiveConstraintsByFieldName(
    configurations,
    appParameters,
    scopeFields,
    formValues,
    selectedArea,
    "AOI_AREA"
  )
  constraints.forEach((constraint) => {
    if (
      "maxValue" in constraint &&
      (settings.maxArea === undefined || settings.maxArea > constraint.maxValue)
    ) {
      settings = {
        ...settings,
        maxArea: constraint.maxValue,
        maxAreaTriggererName: constraint.triggererName,
      }
    }
    if (
      "minValue" in constraint &&
      (settings.minArea === undefined || settings.minArea < constraint.minValue)
    ) {
      settings = {
        ...settings,
        minArea: constraint.minValue,
        minAreaTriggererName: constraint.triggererName,
      }
    }
  })

  return settings
}

export const getActiveConstraintsByFieldName = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues,
  selectedArea: ShapeType,
  fieldName: string,
  isProductionField = false
): any[] => {
  let constraints: any[] = []
  const allFields = getSearchFieldsConfigurationsBy(
      configurations,
      appParameters,
      scopeFields,
      formValues
  );
  allFields.forEach((searchFieldConfigurations) => {
    if (fieldName === searchFieldConfigurations.name) {
      constraints.push({
        ...searchFieldConfigurations,
        triggererName: searchFieldConfigurations.name,
      })
    } else {
      const c = getActiveDependencies(
        fieldName,
        searchFieldConfigurations,
        formValues,
        selectedArea,
        isProductionField
      ).map((dependence: DependenceType) => ({
        ...dependence.constrains,
        triggererName: searchFieldConfigurations.name,
      }))

      if (c.length > 0) {
        constraints = constraints.concat(c)
      }
    }
  })

  return constraints
}

export const isSceneAvailableByStacItem = (
  configurations: ConfigurationsType,
  formValues,
  selectedArea: ShapeType,
  stacItem
) => {
  return !!stacItem.id;
} // @mapitaly: ci sarà sempre, per ricerche su L0 (On-Demand)

export const isCutSceneAvailableByStacItem = (
  configurations: ConfigurationsType,
  formValues,
  selectedArea: ShapeType,
  stacItem
) => {
  return (
    !formValues.routine && // @mapitaly: valido sempre se sono su on-demand
    isSceneAvailableByStacItem(
      configurations,
      formValues,
      selectedArea,
      stacItem
    )
  )
}

export const isGranuleIdAvailableByStacItem = (
  configurations: ConfigurationsType,
  stacItem
) => true

export const isRoutineButtonDisabled = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues,
  selectedArea: ShapeType
) => {
  return (
      ("missions" in configurations &&
      !configurations.missions.some((m) => m.isApplicable)) ||
    !isApplicableFieldFromConfigurations(
      configurations,
      appParameters,
      scopeFields,
      "ROUTINE",
      formValues,
      selectedArea
    )
  )
}

export const getStripProductImagesCount = (
  configurations,
  appParameters,
  formValues,
  selectedArea,
  isInterferometricStackSearch,
  productImages: ProductImageStacType[]
) => {
  const stripProductImages = getStripProductImages(
    configurations,
    appParameters,
    formValues,
    selectedArea,
    isInterferometricStackSearch,
    productImages
  )
  return stripProductImages && stripProductImages.length > 0
    ? stripProductImages.length
    : 0
}

export const hasActiveFlow = (appParameters): boolean | any => {
  // se ho un flow (da appParameters) e sono abilitato a vedere i flow
  let activeFlow: boolean | any;
  let flow = ""
  if (appParameters && appParameters) {
    flow = appParameters.flow;
  }
  switch (flow) {
    case 'invalidation':
      activeFlow = {
        name: 'Invalidation',
        id: 'invalidation',
        tabNumber: 1,
        acceptedFields: ["platform"],
        forcedMandatoryFields: ["platform"]
      }
      break;
    case 'reprocessing':
      activeFlow = {
        name: 'Massive Reprocessing',
        id: 'reprocessing',
        tabNumber: 2,
        acceptedFields: ["platform"],
        forcedMandatoryFields: ["platform"]
      }
      break;
    default:
      activeFlow = false;
  }
  return activeFlow;
};

export const isOperator = (appParameters: AppParametersType): boolean => {
  let isOperator = false;
  const token = sessionStorage.getItem("jwtAuth");
  if (token) {
    const decodedToken: any = jwt_decode(token);
    const clientId: string = process.env.REACT_APP_SSO_CLIENT_ID ? process.env.REACT_APP_SSO_CLIENT_ID : "frontend_cms";
    if (decodedToken.resource_access && decodedToken.resource_access[clientId] && decodedToken.resource_access[clientId].roles) {
      const roles: string[] = decodedToken.resource_access[clientId].roles;
      isOperator = roles.some((role: string) => role.toLowerCase() === "operatore");
    }
  }
  return isOperator;
}

export const getBoundsNumberOfSelectedInterferometricStacksForProcessingParametersForm =
  (configurations: ConfigurationsType, appParameters: AppParametersType, formValues): [number, number | null] => {
    const selectedImagesConfig = getSearchFieldConfigurationsBy(
      configurations,
      appParameters,
      ADVANCED_SEARCH_SCOPE_CONFIGURATION_FIELDS,
      "SELECTED_STACKS",
      formValues
    ) as NumberSearchFieldType | undefined

    let minSelectedStacks = 1
    let maxSelectedStacks
    if (
      selectedImagesConfig &&
      isApplicableField(selectedImagesConfig)
    ) {
      minSelectedStacks = selectedImagesConfig.minValue || 1
      maxSelectedStacks = selectedImagesConfig.maxValue
    }

    return [minSelectedStacks, maxSelectedStacks]
  }

export const getDefaultOrderByParams = (
  configurations: ConfigurationsType,
  formValues: any,
  isInterferometricStackSearch: boolean = false,
  language
) => {
  const defaultFieldName = isInterferometricStackSearch
    ? "identifier"
    : "start_datetime"
  const sortFields = getSortFieldsFrom(
    configurations,
    isInterferometricStackSearch,
    language
  )
  const field =
    sortFields.find((sortField) => sortField.name === defaultFieldName) ||
    sortFields[0]

  return {
    fieldName: field.name,
    sortType: field.type === "date" && formValues.routine ? "DESC" : "ASC",
  }
}

export const getMinDaysOffsetBy = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues,
  selectedArea: ShapeType
): number => {
  return (
    getActiveConstraintsByFieldName(
      configurations,
      appParameters,
      scopeFields,
      formValues,
      selectedArea,
      "DAYS_OFFSET"
    )
      .map((constraint) => constraint.minValue)
      .reduce((acc, nextValue) => {
        return acc < nextValue ? nextValue : acc
      }, 0) || 0
  )
}

export const getSummaryOrderParameters = (
  mainInputConfigurations: any,
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeConfigurationFields: string,
  selectedArea: ShapeType,
  values: any,
  order: CartOrderType,
  cartName: string,
  customerOrderNumber: string
) => {
  const summaryOrderParameters: any = {}
  if (!!cartName) {
    summaryOrderParameters.tags = [cartName]
  }
  if (!!customerOrderNumber) {
    summaryOrderParameters.customerOrderNumber = [customerOrderNumber]
  }


  const missions = getMissionCodesByOrder(configurations, order)
  if (missions && missions.length === 1) {
    const finalValues: any = extractFinalValues(
      mainInputConfigurations,
      configurations,
      appParameters,
      scopeConfigurationFields,
      selectedArea,
      {
        ...values,
        missions,
      }
    )
    summaryOrderParameters.searchFilters = {
      ...(order.searchFilters || {}),
      ...(extractFiltersBy(
        configurations,
        appParameters,
        mainInputConfigurations,
        scopeConfigurationFields,
        finalValues
      ) || {}),
    }
  }

  console.log('summaryOrderParameters',summaryOrderParameters);

  return {
    ...order,
    ...summaryOrderParameters,
  }
}
